<template>
  <a
    v-if="visible"
    class="empty-value-trigger"
    :aria-label="$t('filter.empty-value-add')"
    :title="$t('filter.empty-value-add')"
    tabindex="0"
    @click="onAdd"
    @keyup.enter="onAdd"
  >
    + {{ label }}
  </a>
</template>

<script lang="ts">
import { computed } from "vue";
import i18n from "@/i18n";

/**
 * Bouton réutilisable "+ (vide)" pour ajouter un filtre sur la valeur
 * vide ( null en base ) à n'importe quel composant filtre de colonne .
 *
 * <p>Source de vérité unique : utilisé par {@link TextFilter} et
 * {@link ReferenceFilter} pour offrir un UX cohérent ( label i18n ,
 * accessibilité , style ) sans dupliquer le markup ni la logique de
 * visibilité conditionnelle .
 *
 * <p>Contrat :
 * <ul>
 *   <li>{@code hasEmpty} ( true / false ) : indique si la colonne
 *       contient au moins une valeur null en base . Calculé côté
 *       backend par {@code DataRepository.getColumnHasEmpty} ;
 *   <li>{@code disabled} ( true / false ) : masque le bouton quand
 *       la valeur "(vide)" est déjà sélectionnée , ou quand la
 *       colonne porte une option unique pré-sélectionnée .
 * </ul>
 *
 * <p>L'événement {@code add} est émis sans payload ; c'est au parent
 * de décider comment ajouter la valeur "(vide)" à sa sélection
 * interne ( cf. §5.7 de FILTER_TEXT_LIST.md ) .
 */
export default {
  name: "EmptyValueButton",
  props: {
    /**
     * Vrai si la colonne contient au moins une valeur null en base
     * ( drapeau remonté par le backend via {@code hasEmpty} ) . Sans
     * ce drapeau le bouton n'est pas rendu pour éviter de proposer
     * une option qui n'aurait jamais de match .
     */
    hasEmpty: { type: Boolean, default: false },
    /**
     * Vrai si le bouton doit être masqué pour des raisons d'état
     * ( valeur "(vide)" déjà sélectionnée , ou colonne single-option
     * en mode auto-select non modifiable ) .
     */
    disabled: { type: Boolean, default: false },
  },
  emits: ["add"],
  setup(props, { emit }) {
    const visible = computed(() => props.hasEmpty && !props.disabled);
    const label = computed(() => i18n.global.t("filter.empty-value-label"));
    function onAdd() {
      if (visible.value) emit("add");
    }
    return { visible, label, onAdd };
  },
};
</script>
